import React, { createContext, useContext, useMemo } from 'react';
import {
    FEATURE_FLAGS,
    getCurrentEnvironment,
    isFeatureEnabled,
    getFeatureFlag,
    getAllFeatureFlags,
} from './featureFlags.config';
import type { FeatureFlagContextType, FeatureFlagProviderProps } from './types';

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

/**
 * Provider component that makes feature flag state available throughout the app.
 * Should be placed high in the component tree, before components that need feature flags.
 *
 * @example
 * <FeatureFlagProvider>
 *   <App />
 * </FeatureFlagProvider>
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
    children,
    overrideEnvironment,
}) => {
    const environment = overrideEnvironment || getCurrentEnvironment();

    const contextValue: FeatureFlagContextType = useMemo(
        () => ({
            isEnabled: (featureName: string): boolean => {
                return isFeatureEnabled(featureName);
            },

            getAllFlags: (): Record<string, boolean> => {
                return getAllFeatureFlags();
            },

            getFlag: (featureName: string) => {
                return getFeatureFlag(featureName);
            },

            environment,

            // Future: Role-based access checking
            // Currently returns true - will be implemented when security is added
            hasRoleAccess: (featureName: string, userRole?: string): boolean => {
                const flag = FEATURE_FLAGS[featureName];
                if (!flag) return false;

                // If no roles defined, feature is available to all
                if (!flag.allowedRoles || flag.allowedRoles.length === 0) {
                    return true;
                }

                // Future: Check user role against allowedRoles
                // For now, return true to not block features
                // TODO: Implement actual role checking when security is added
                // Example future implementation:
                // if (!userRole) return false;
                // return flag.allowedRoles.includes(userRole);
                return true;
            },
        }),
        [environment]
    );

    return (
        <FeatureFlagContext.Provider value={contextValue}>
            {children}
        </FeatureFlagContext.Provider>
    );
};

/**
 * Hook to access feature flag context.
 * Must be used within a FeatureFlagProvider.
 *
 * @throws Error if used outside of FeatureFlagProvider
 *
 * @example
 * const { isEnabled, environment } = useFeatureFlagContext();
 * if (isEnabled('new-dashboard')) {
 *   // render new dashboard
 * }
 */
export const useFeatureFlagContext = (): FeatureFlagContextType => {
    const context = useContext(FeatureFlagContext);
    if (context === undefined) {
        throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
    }
    return context;
};
